import React from 'react';
import PropTypes from 'prop-types';
import polyline from '@mapbox/polyline';
import simplify from "@turf/simplify";
import imageOpenInNew from '../../components/pages/dashboard/EventMedia/images/open-in-new-w64.svg';
import MapboxControl from '../mapbox/mapboxStaticImage/MapboxControl';
import EventMediaPreloaderUnderlay from '../pages/dashboard/EventMedia/EventMediaPreloaderUnderlay';
import lsUtil from '../common/lsUtil';
import { strings } from '../../localization';
import { locationIQToken, LOCATIONIQ_BASE_STATIC_URL } from '../../services/locationService';
import {RAVEN_EVENTS_AI, RAVEN_EVENTS_PROBLEM, RAVENS_EVENTS_ENGINE, RAVENS_EVENTS_GEOFENCE} from '../common/EventsUtils'


//import { requestEventMediaFullscreen } from '../../../components/pages/dashboard/EventMedia/EventMedia';

const SIMPLIFY_TOLERANCES = [0.0005, 0.001, 0.002, 0.005, 0.01, 0.02, 0.05, 0.1, 0.2, 0.5, 1];

function simplifyPoints(gpsPointsLatLon, tolerance = SIMPLIFY_TOLERANCES[0]) { // expects [[lat, lon]]
    if (!gpsPointsLatLon || gpsPointsLatLon.length === 0) {
        return [];
    }

    // Convert to [lng, lat] for Turf.js
    const gpsPointsLngLat = gpsPointsLatLon.map(p => [p[1], p[0]]);

    const geojsonLine = {
        type: 'Feature',
        geometry: {
            type: 'LineString',
            coordinates: gpsPointsLngLat,
        },
    };

    // simplify can throw if coordinates are not valid, e.g. too few points for a LineString
    let simplified;

    try {
        simplified = simplify(geojsonLine, { tolerance, highQuality: false });
    }
    catch (error) {
        console.error("LocationIQ Static Image :Error during simplification", error);
        // return original points if simplification fails
        return gpsPointsLatLon;
    }

    if (!simplified || !simplified.geometry || !simplified.geometry.coordinates) {
        return gpsPointsLatLon;
    }

    // convert back to [lat, lon]
    return simplified.geometry.coordinates.map(p => [p[1], p[0]]);
}

export function getLocationIQEventMarkerColor(eventType) {
    if (RAVEN_EVENTS_AI.includes(eventType)) {
        return 'blue';
    } else if (RAVENS_EVENTS_ENGINE.includes(eventType)){
        return 'green';
    }else if (RAVENS_EVENTS_GEOFENCE.includes(eventType)){
        return 'gray';
    } else if (RAVEN_EVENTS_PROBLEM.includes(eventType)){
        return 'red';
    } else {
        return 'yellow';
    }
}

export default class LocationIQStaticImage extends React.PureComponent {

    fullscreenElementRef = React.createRef();

    constructor(props) {
        super(props);

        this.state = {
            imageUrl: null, // Current URL for the img tag
            isLoading: true, // True if an image is currently being loaded
            actualImageFailed: false, // True if the primary/actual image load attempt failed
            fallbackImageFailed: false, // True if the fallback image load attempt also failed
        };
    }

    get locationIQBaseURL() {
        let selectedTheme = lsUtil.getItem('raven.theme');
        if (!selectedTheme) {
            selectedTheme = this.props.defaultTheme || 'light';
        }
        return `${LOCATIONIQ_BASE_STATIC_URL}?key=${locationIQToken}&maptype=${selectedTheme}&size=${this.props.width}x${this.props.height}`
    }

    get defaultLocationIQURL() {
        return `${this.locationIQBaseURL}&center=47.3,-96.8&zoom=3`;
    }

    componentDidMount() {

        if (!this.props.isWaitingForGPSData) {
            this.setImageUrl();
        }
    }

    componentDidUpdate(prevProps) {
        const wasWaiting = prevProps.isWaitingForGPSData;
        const isWaiting = this.props.isWaitingForGPSData;


        if ((wasWaiting && !isWaiting)) {
            this.setImageUrl();
        }
    }

    setImageUrl = () => {
        const { hasGPSData, points } = this.props;

        if (!hasGPSData || !Array.isArray(points) || points.length === 0) {
            // No GPS data to generate an "actual" image from path, or no points.
            // Attempt to load the default image and show "no GPS data" message.
            console.warn("LocationIQStaticImage: No GPS data or card points, using default image");
            this.setState({
                imageUrl: this.defaultLocationIQURL, // Set to default image URL
                isLoading: true,                 // Start loading the default image
                actualImageFailed: true,         // Consider actual image as "failed" or "not applicable"
                fallbackImageFailed: false,        // Fallback (default image) is being attempted
            });
            return;
        }

        // Has GPS data, attempt to load the "actual" image.
        const actualImageUrl = this.generateImageUrl();
        this.setState({
            imageUrl: actualImageUrl,
            isLoading: true,
            actualImageFailed: false,
            fallbackImageFailed: false,
        });
    }

    handleImageLoad = () => {
        this.setState({
            isLoading: false
            // If actualImageFailed is true here, it means the fallback loaded.
            // If actualImageFailed is false, it means the actual image loaded.
        });
    }

    handleImageError = () => {
        if (!this.state.actualImageFailed) {
            // The "actual" image failed to load.
            console.error("LocationIQStaticImage: Error loading actual image. Attempting fallback.");
            this.setState({
                actualImageFailed: true,
                imageUrl: this.defaultLocationIQURL, // Switch to fallback URL
                isLoading: true, // Start loading the fallback
                fallbackImageFailed: false, // Reset this as we are now attempting fallback
            });
        } else {
            // The "fallback" image also failed to load (actualImageFailed is already true).
            console.error("LocationIQStaticImage: Error loading fallback image. All attempts failed.");
            this.setState({
                // actualImageFailed remains true
                fallbackImageFailed: true,
                isLoading: false, // Stop loading attempts
                imageUrl: null, // No image to display
            });
        }
    }

    requestEventMapBoxImageGPSData = () => {
        // Add null check for this.props.marker
        if (this.props.marker && typeof this.props.marker.latitude === 'number' && typeof this.props.marker.longitude === 'number') {
            const googleMapsURL = `https://www.google.com/maps/search/?api=1&query=${this.props.marker.latitude},${this.props.marker.longitude}`;
            window.open(googleMapsURL, '_blank');
        } else {
            console.warn("MapboxStaticImage: Marker data is not available or invalid to open in Google Maps.");
            // Optionally, provide user feedback here if appropriate
        }
    };

    generateImageUrl = (simplify = false, round = -1) => {

        const { marker, points } = this.props;

        const reversedPoints = points.slice().reverse();
        let extractedPoints = reversedPoints.map(point => [point.lat, point.lon]);

        if (!Array.isArray(extractedPoints) || extractedPoints.length === 0) {
            console.warn("LocationIQStaticImage: No GPS data or card points, using default image");
            return this.defaultLocationIQURL;
        }

        if (simplify) {
            if (round >= 0 && round < SIMPLIFY_TOLERANCES.length) {
                const tolerance = SIMPLIFY_TOLERANCES[round];
                extractedPoints = simplifyPoints(extractedPoints, tolerance);
            }
        }

        // Convert path to LocationIQ format: lat,lon|lat,lon|lat,lon
        const pathCoords = extractedPoints.map(p => `${p[0]},${p[1]}`).join('|');

        // Marker format: markers=lat,lon,color
        const markerParam = marker
            ? `&markers=icon:small-${marker.color}-cutout|${marker.latitude},${marker.longitude}`
            : '';

        // Path format
        const pathParam = `&path=weight:3|color:blue|${pathCoords}`;

        let locationIQUrl = `${this.locationIQBaseURL + markerParam + pathParam}`;

        // URL length safety
        if (locationIQUrl.length > 8000 && round < SIMPLIFY_TOLERANCES.length - 1) {
            return this.generateImageUrl(true, round + 1);
        }

        return locationIQUrl;
    }

    render() {
        const isDark = lsUtil.getItem('raven.theme') === 'dark';

        const { hasGPSData } = this.props;

        const imageControls = (
            <div className="event-media-controls">
                <div className="pop-out-control">
                    <MapboxControl
                        id="EVENT_MAPBOX_IMAGE_CONTROL_POP_OUT"
                        iconSrc={imageOpenInNew}
                        render={true}
                        pending={this.state.isLoading}
                        onClick={() => { this.requestEventMapBoxImageGPSData() }}
                    />
                </div>
            </div>
        );

        return (
            <div className="col-12 image-panel panel-wrapper">

                <div className="row section-wrapper event-mapbox-image">
                    <div className="col-12 section-body">
                        <div className="event-media-max-width-480">
                            <div className="event-media-wrapper-16-9-aspect-ratio" ref={this.fullscreenElementRef}>
                                <div className="event-media-wrapper">
                                    <EventMediaPreloaderUnderlay status={this.state.fallbackImageFailed ? "ERROR" : undefined} />

                                    {this.state.imageUrl && !this.state.fallbackImageFailed &&
                                        <img
                                            key={this.state.imageUrl}
                                            style={{
                                                display: this.state.isLoading ? 'none' : 'block',
                                                filter: isDark ? 'brightness(140%) contrast(90%)' : 'none'
                                            }}
                                            src={this.state.imageUrl}
                                            className="mapbox-static-image"
                                            alt="event location on map"
                                            onLoad={this.handleImageLoad}
                                            onError={this.handleImageError}
                                        />
                                    }

                                    {/* Conditional rendering for messages and controls */}
                                    {!this.state.isLoading && (
                                        <>
                                            {/* Case 1: Fallback image (default map) also failed. Show error, no image. */}
                                            {this.state.fallbackImageFailed && (
                                                <div className="event-media-message-overlay">
                                                    <h3>{strings.mapbox_static_image_error}</h3>
                                                </div>
                                            )}

                                            {/* Case 2: Fallback image is shown (either because actual failed or no GPS data). */}
                                            {!this.state.fallbackImageFailed && this.state.actualImageFailed && (
                                                <>
                                                    {!hasGPSData ? (
                                                        // No GPS data, but fallback (default map) is shown.
                                                        <div className="event-media-message-overlay">
                                                            <h3>{strings.mapbox_static_image_no_gps_data_available}</h3>
                                                        </div>
                                                    ) : (
                                                        // Actual image (with GPS data) failed, fallback (default map) is shown.
                                                        <div className="event-media-message-overlay">
                                                            <h3>{strings.mapbox_static_image_error}</h3>
                                                        </div>
                                                    )}
                                                </>
                                            )}

                                            {/* Case 3: Actual image (specific map with GPS data) is shown. */}
                                            {!this.state.fallbackImageFailed && !this.state.actualImageFailed && hasGPSData && this.state.imageUrl && (
                                                imageControls
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}

LocationIQStaticImage.propTypes = {
    width: PropTypes.number.isRequired,
    height: PropTypes.number.isRequired,
    marker: PropTypes.shape({
        latitude: PropTypes.number.isRequired,
        longitude: PropTypes.number.isRequired,
        color: PropTypes.string
    }),
    points: PropTypes.array,
    hasGPSData: PropTypes.bool.isRequired,
    defaultTheme: PropTypes.string,
};
